import { AdminRole, ADMIN_ROLES } from '@/src/types';

export const ADMIN_AREAS = [
  { id: 'dashboard', labelKey: 'adminPage.dashboard.dashboard', supportsWrite: false },
  { id: 'events', labelKey: 'adminPage.dashboard.manageEvents', supportsWrite: true },
  { id: 'bookings', labelKey: 'adminPage.dashboard.manageBookings', supportsWrite: true },
  { id: 'maintenanceTasks', labelKey: 'maintenanceTasks.title', supportsWrite: true },
  { id: 'donations', labelKey: 'adminPage.dashboard.manageDonations', supportsWrite: true },
  { id: 'donationRequests', labelKey: 'adminPage.dashboard.manageDonationRequests', supportsWrite: true },
  // Status transitions only (Approve / Mark Fulfilled / Reject / Reset to
  // Pending) — separate from 'donationRequests', which gates viewing and
  // editing request details. A role can hold one without the other, e.g.
  // trustMember edits details but cannot approve. Enforced server-side in
  // DonationRequestLifecycle's "transition" command via
  // SecurityService.hasAreaWriteAccess(role, 'donationApprovals').
  { id: 'donationApprovals', labelKey: 'adminPage.dashboard.donationApprovals', supportsWrite: true },
  { id: 'donationReport', labelKey: 'adminPage.dashboard.donationReport', supportsWrite: false },
  { id: 'monkVisits', labelKey: 'adminPage.dashboard.manageMonkVisits', supportsWrite: true },
  { id: 'vasTeams', labelKey: 'vasTeams.tabLabel', supportsWrite: true },
  { id: 'vasBudget', labelKey: 'vasBudget.tabLabel', supportsWrite: true },
  { id: 'users', labelKey: 'adminPage.dashboard.manageUsers', supportsWrite: true },
  { id: 'emailTemplates', labelKey: 'adminPage.dashboard.manageEmailTemplates', supportsWrite: true },
  { id: 'emailMonitoring', labelKey: 'adminPage.dashboard.manageEmailMonitoring', supportsWrite: true },
  { id: 'contacts', labelKey: 'adminPage.dashboard.manageContacts', supportsWrite: true },
  { id: 'siteSettings', labelKey: 'adminPage.dashboard.manageSiteSettings', supportsWrite: true },
  { id: 'auditLogs', labelKey: 'adminPage.dashboard.auditLogs', supportsWrite: false },
  { id: 'sheetSync', labelKey: 'sheetSync.tabLabel', supportsWrite: false },
  // Self-service profile page. Every authenticated admin — regardless of role
  // or delegated permissions — can always see and edit their own doc; the
  // Firestore rule (samadhi_admins self-update whitelist) is the real gate.
  { id: 'profile', labelKey: 'adminPage.dashboard.myProfile', supportsWrite: true },
] as const;

export type AdminArea = typeof ADMIN_AREAS[number]['id'];

export type AdminAreaPermission = {
  read: boolean;
  write: boolean;
};

export type RolePermissions = Record<AdminArea, AdminAreaPermission>;
export type RolePermissionsByRole = Record<AdminRole, RolePermissions>;

const writable = (read = true): AdminAreaPermission => ({ read, write: read });
const readOnly = (read = true): AdminAreaPermission => ({ read, write: false });
const hidden = (): AdminAreaPermission => ({ read: false, write: false });

const allAreas = (permission: AdminAreaPermission): RolePermissions =>
  Object.fromEntries(ADMIN_AREAS.map(area => [
    area.id,
    { read: permission.read, write: area.supportsWrite === false ? false : permission.write },
  ])) as RolePermissions;

export const DEFAULT_ROLE_PERMISSIONS: RolePermissionsByRole = {
  admin: allAreas(writable()),
  // Money-focused role: writes on donations + budget, reads what's needed
  // for financial context (bookings and events drive donation categories
  // and paid enrolments). Everything else is hidden so a finance user
  // cannot edit user accounts, site settings, monk visits, or comms.
  finance: {
    dashboard: readOnly(),
    events: readOnly(),
    bookings: readOnly(),
    maintenanceTasks: hidden(),
    donations: writable(),
    donationRequests: writable(),
    // Matches the pre-matrix hardcoded rule: only admin and finance could
    // transition a donation request's status. See DEFAULT_ROLE_PERMISSIONS
    // comment on 'donationApprovals' above and SecurityService's fallback
    // for role docs saved before this area existed.
    donationApprovals: writable(),
    donationReport: readOnly(),
    monkVisits: hidden(),
    vasTeams: hidden(),
    vasBudget: writable(),
    users: hidden(),
    emailTemplates: hidden(),
    emailMonitoring: hidden(),
    contacts: hidden(),
    siteSettings: hidden(),
    auditLogs: hidden(),
    sheetSync: hidden(),
    profile: writable(),
  },
  trustMember: {
    dashboard: readOnly(),
    events: writable(),
    bookings: writable(),
    maintenanceTasks: writable(),
    donations: writable(),
    donationRequests: writable(),
    // trustMember may edit request details but never held approval rights.
    donationApprovals: hidden(),
    donationReport: readOnly(),
    monkVisits: writable(),
    vasTeams: readOnly(),
    vasBudget: readOnly(),
    users: hidden(),
    emailTemplates: hidden(),
    emailMonitoring: hidden(),
    contacts: writable(),
    siteSettings: hidden(),
    auditLogs: hidden(),
    sheetSync: hidden(),
    profile: writable(),
  },
  coordinator: {
    dashboard: readOnly(),
    events: writable(),
    bookings: writable(),
    maintenanceTasks: writable(),
    donations: hidden(),
    donationRequests: readOnly(),
    donationApprovals: hidden(),
    donationReport: hidden(),
    monkVisits: writable(),
    vasTeams: readOnly(),
    vasBudget: hidden(),
    users: hidden(),
    emailTemplates: hidden(),
    emailMonitoring: hidden(),
    contacts: readOnly(),
    siteSettings: hidden(),
    auditLogs: hidden(),
    sheetSync: hidden(),
    profile: writable(),
  },
  supporter: {
    dashboard: readOnly(),
    events: readOnly(),
    bookings: readOnly(),
    maintenanceTasks: readOnly(),
    donations: readOnly(),
    donationRequests: readOnly(),
    donationApprovals: hidden(),
    donationReport: readOnly(),
    monkVisits: readOnly(),
    vasTeams: readOnly(),
    vasBudget: hidden(),
    users: hidden(),
    emailTemplates: hidden(),
    emailMonitoring: hidden(),
    contacts: hidden(),
    siteSettings: hidden(),
    auditLogs: hidden(),
    sheetSync: hidden(),
    profile: writable(),
  },
};

export function normalizeRolePermissions(input?: Partial<RolePermissions>): RolePermissions {
  const base = allAreas(hidden());
  for (const area of ADMIN_AREAS) {
    const value = input?.[area.id];
    base[area.id] = {
      read: Boolean(value?.read),
      write: area.supportsWrite === false ? false : Boolean(value?.write && value?.read),
    };
  }
  return base;
}

export function mergeRolePermissions(
  input?: Partial<Record<AdminRole, Partial<RolePermissions>>>
): RolePermissionsByRole {
  return Object.fromEntries(ADMIN_ROLES.map(role => [
    role,
    normalizeRolePermissions({
      ...DEFAULT_ROLE_PERMISSIONS[role],
      ...(input?.[role] || {}),
    }),
  ])) as RolePermissionsByRole;
}

export function canReadArea(
  permissions: RolePermissionsByRole,
  role: AdminRole,
  area: AdminArea
): boolean {
  return permissions[role]?.[area]?.read === true;
}

export function canWriteArea(
  permissions: RolePermissionsByRole,
  role: AdminRole,
  area: AdminArea
): boolean {
  const permission = permissions[role]?.[area];
  return permission?.read === true && permission.write === true;
}
